// warehouse/screenSources.js — los ensambladores de pantalla de la bodega
// (F6.4b §5). Cada ensamblador devuelve EXACTAMENTE el shape que el fetch
// vivo de su pantalla produce (contrato intacto: la pantalla no distingue de
// dónde vino el dato) — más una llave `offline: true` en los shapes-objeto,
// que es lo que la pantalla usa para deshabilitar los CTAs que requieren red.
// Prohibido stale-then-swap: el dato de bodega entra por el camino normal del
// fetcher (resolución del mismo Promise), nunca como segundo render encima.
//
// Guardia de dueño: TODOS los ensambladores exigen que owner_user_id sea el
// userId de la sesión que pide el dato — la bodega de otro usuario jamás
// pinta una pantalla (misma regla de privacidad que las purgas de F6.4a).
//
// Solo se usan cuando el fetch vivo cayó con isNetworkShapedError en el build
// nativo; un fetch vivo exitoso se comporta EXACTAMENTE como hoy.
//
// RPW T5 (P3) añade al final una especie NUEVA: `examDeliveriesFromWarehouse` +
// `mergeExamDeliveries` no SUSTITUYEN un fetch caído — se FUSIONAN encima de uno
// exitoso, porque lo que el cel ya sabe (una entrega que el cartero aún no ha
// subido) tiene que verse en la primera visita. Misma guardia de dueño, misma
// promesa de no lanzar nunca, y con identidad: sin nada que fusionar, el objeto
// del servidor sale tal cual.

import { isNativePlatform, openWarehouse } from './connection';
import { SYNC_META_KEYS } from './schema';
import { readPlanSnapshot, readDocsSnapshot } from './snapshots';
import { esIdDeCard, gruposSinRed, leerGruposDeBodega } from './cardsDelGrupo';
import { log as diagLog } from '../diag/readyChainLog';
import { emitScreenCorrected } from '../screenEvents';
import { sortLibraryDocs } from '../sortLibraryDocs';
// TANDA 29 (#274) — la regla de tema, la MISMA que usa el recorte del pool.
import { claveDeTema } from '../claveDeTema';

function parseJson(raw, fallback) {
  if (raw === null || raw === undefined) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed === null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
}

// Bodega abierta Y del dueño correcto, o null. Nunca lanza.
async function openOwnedWarehouse(userId) {
  if (!isNativePlatform() || !userId) return null;
  try {
    const api = await openWarehouse();
    const rows = await api.query(
      'SELECT value FROM sync_meta WHERE key = ?;',
      [SYNC_META_KEYS.OWNER_USER_ID]
    );
    const owner = rows.length > 0 && rows[0].value ? rows[0].value : null;
    return owner === userId ? api : null;
  } catch (e) {
    return null;
  }
}

// Fila de `documents` → el doc que las pantallas pintan (mismos campos que
// el catálogo vivo usa; la bodega solo guarda lo que /sync/bundle sirve).
function rowToDoc(row) {
  return {
    id: row.id,
    filename: row.filename,
    file_type: row.file_type,
    status: row.status,
    topics: parseJson(row.topics_json, []),
    question_count: row.question_count,
    flashcard_count: row.flashcard_count,
    detected_language: row.detected_language,
    parent_doc_id: row.parent_doc_id,
    created_at: row.created_at,
    // F6.5a: sin esta columna las partes de un split llegaban undefined y
    // sortLibraryDocs degradaba al orden de inserción de SQLite (censo E.5).
    chunk_index: row.chunk_index,
  };
}

async function readDocs(api) {
  // TANDA M (M6): `locked_at IS NULL` — los BLOQUEADOS no se pintan.
  //
  // Hasta esta tanda la bodega sólo contenía servibles y por eso este SELECT no
  // filtraba nada; el comentario que vivía aquí decía justo eso. Desde v12 un
  // documento archivado por una bajada de plan SE CONSERVA (con su pool entero,
  // para que subir de plan lo reviva sin re-descargar) y se marca con
  // `locked_at`. Invisible mientras el plan no lo cubra: esta línea es la mitad
  // "invisible" de esa promesa, y `studyAvailability` es la mitad "inusable".
  // El filtro de status sigue siendo de fábrica: lo que baja es ready+partial.
  const rows = await api.query('SELECT * FROM documents WHERE locked_at IS NULL;');
  // t7-3 (A3): una card del profe vive en la bodega como el documento `pub:<id>`, pero no
  // es un documento del alumno: las listas de "tus documentos" no la pintan (la pinta la
  // tarjeta del grupo). Sin cards, el filtro no quita nada.
  return sortLibraryDocs(rows.filter((r) => !esIdDeCard(r.id)).map(rowToDoc));
}

// ── F6.8 T2 — EL PUENTE AL MOTOR ────────────────────────────────────────────
//
// Los ensambladores de analíticas y la nueva fusión del Study Log NO calculan
// nada: leen la bodega, arman el payload del contrato y se lo dan al cerebro
// (lib/brain/analyticsReads.js → driver.py). El import es DINÁMICO, el mismo
// patrón con el que el bajador entra al motor (downloader.js:400-403): la web no
// pide ese chunk jamás, y en el build de app solo se carga cuando una pantalla
// de verdad necesita la lectura local.
async function brainReads() {
  return import('../brain/analyticsReads');
}

/**
 * D-1 — EL DISPARO DEL CIERRE DE VENCIDOS, fire-and-forget.
 *
 * La lectura descubre que un examen NACIDO EN EL TELÉFONO ya venció; la escritura
 * la hace el CARRIL DEL EXAMEN, por su camino de auto-entrega de siempre
 * (`settleExpiredLocalExams` → `autoSubmitExpiredInFlight`). La pintada NO
 * espera: la vista ya sale sellada por el gemelo de vencidos del driver, así que
 * el usuario ve el número correcto aunque esta escritura tarde o falle.
 *
 * Sin nada que sellar no se importa ni un byte del carril. Cero timers, cero
 * polling: esto solo ocurre cuando una lectura de pantalla lo descubre.
 */
function fireSettleExpiredExams(userId, now, expiredIds) {
  if (!expiredIds || expiredIds.length === 0) return;
  Promise.resolve()
    .then(() => import('../brain/examRuntime'))
    .then((m) => m.settleExpiredLocalExams(userId, now))
    .catch(() => { /* el siguiente descubrimiento lo reintenta */ });
}

// ── F6.8 S-2 (pieza 3) — EL RASTRO Y LA CORRECCIÓN DE LA FOTO DE AYER ───────
//
// EL HUECO QUE CIERRA (censo S2-C11 variante ii, HUECO #2): «el fallback no deja
// rastro. Un Study Log offline servido por la foto de ayer es INDISTINGUIBLE de
// uno servido por el motor.» Diagnosticar desde Windows era a ciegas: la foto no
// fusiona entregas locales ni sella vencidos, así que puede MENTIR — y mentía en
// silencio. `diagLog` es no-op TOTAL en web (gate IS_APP_BUILD), jamás lanza y
// jamás cambia el orden de nada.

// El motivo del rechazo, en UNA palabra, derivado del mensaje del error. El
// warm-up es el RPC `init`, así que su nombre lo delata; un vencimiento de
// presupuesto dice `timed out`; cualquier otra cosa es `error` — y es honesto que
// lo sea: desde aquí no hay más evidencia que ese mensaje.
function motivoDelRechazo(e) {
  const msg = String((e && e.message) || e || '');
  if (msg.includes("'init'")) return 'warm-up';
  if (msg.includes('timed out')) return 'timeout';
  return 'error';
}

// UNA corrección a la vez, y jamás en bucle. El reintento llama a
// `studyLogViaBrain` DIRECTO —no a `studyLogFromWarehouse`—, así que si vuelve a
// fallar no lee la foto ni agenda otra corrección: por construcción no hay ciclo.
let correccionEnVuelo = false;

/**
 * LA CORRECCIÓN: al primer éxito posterior del motor para ESTA misma lectura, la
 * verdad se re-escribe y la pantalla montada se corrige pintando UNA vez.
 *
 * MECÁNICA, la más chica que cumple: un reintento fire-and-forget colgado del
 * MISMO descubrimiento que sirvió la foto — exactamente el patrón de
 * `fireSettleExpiredExams`, que ya vive arriba. CERO timers, cero polling, cero
 * despertares: no hay `setTimeout`, no hay intervalo y no hay oído de red nuevo.
 * Si el worker seguía frío y su primer RPC se venció, este segundo intento cae
 * sobre un `warmUp()` que ya arrancó de nuevo y suele responder; si el motor está
 * roto de verdad, el intento falla en silencio y no se agenda nada más.
 *
 * POR QUÉ ESTO NO ES EL "stale-then-swap" QUE EL CACHE PROHÍBE: aquel es pintar
 * dato viejo que luego brinca al llegar el fresco por el camino NORMAL. Aquí lo
 * que se pintó es una red de emergencia declarada que puede estar MINTIENDO, y
 * dejar la mentira en pantalla es peor que corregirla una vez. Autorizado en la
 * firma de la pieza 3, con su límite escrito: UNA corrección, sin parpadeo, sin
 * bucles.
 */
function fireStudyLogCorrection(api, userId) {
  if (correccionEnVuelo) return;
  correccionEnVuelo = true;
  Promise.resolve()
    .then(() => studyLogViaBrain(api, userId))
    .then((verdad) => {
      if (verdad === null || verdad === undefined) return;
      diagLog('SCR.RECOVER', { pantalla: 'study-log', filas: verdad.length });
      emitScreenCorrected('study-log', verdad);
    })
    .catch(() => { /* el motor sigue sin arrancar: la próxima lectura reintenta */ })
    .finally(() => { correccionEnVuelo = false; });
}

// study_history fila → la entrada EXACTA de GET /study/history (por modo,
// como el handler del server: exam lleva total_questions/time_limit,
// adaptive lleva total_answered).
//
// F6.8 T2 — YA NO ES EL CAMINO DEL STUDY LOG: la fusión (D-F8-4) lo resuelve por
// el motor. Su consumidor VIVO es `studyCenterFromWarehouse`, que solo cuenta
// sesiones completadas y no necesita ni la fusión ni el sello de vencidos. No se
// deprecó ni se borró: sigue teniendo dueño.
//
// F6.8 T2b — Y ES ADEMÁS LA RED DE DISPONIBILIDAD del Study Log (cierre de C-1).
// Cuando el motor RECHAZA, `studyLogFromWarehouse` cae aquí (ver el fallback en
// esa función). LO QUE ESTA VISTA NO TIENE, y hay que saberlo al leerla:
//   · NO fusiona pendientes locales — una sesión hecha sin señal que el servidor
//     aún no conoce NO sale;
//   · NO aplica el sello de vencidos — un examen cuyo reloj se acabó sigue
//     pintándose "en curso";
//   · el score adaptive y `document_names` son los PRECOCINADOS de la foto, no
//     los que el núcleo deriva.
// Es la FOTO DE AYER, y esa es exactamente su función: que la pantalla exista
// cuando el motor no arranca. La regla de una-sola-verdad no se rompe — hay UNA
// verdad (el motor) y una red de emergencia DECLARADA que jamás la sustituye
// cuando la verdad está disponible.
function rowToHistorySession(row) {
  const entry = {
    id: row.id,
    mode: row.mode,
    document_ids: parseJson(row.document_ids_json, []),
    started_at: row.started_at,
    completed_at: row.completed_at,
    completed: Boolean(row.completed),
    status: row.status,
  };
  if (row.mode === 'exam') {
    entry.score = row.score;
    entry.correct_count = row.correct_count ?? 0;
    entry.total_questions = row.total_questions ?? 0;
    entry.time_limit = row.time_limit;
  } else if (row.mode === 'adaptive') {
    entry.total_answered = row.total_answered ?? 0;
    entry.correct_count = row.correct_count ?? 0;
    entry.score = row.score;
  }
  entry.document_names = parseJson(row.document_names_json, []);
  return entry;
}

async function readHistory(api) {
  // Mismo orden que el server: started_at desc (get_study_history).
  const rows = await api.query(
    'SELECT * FROM study_history ORDER BY started_at DESC;'
  );
  return rows.map(rowToHistorySession);
}

/** Study Center: el shape de fetchStudyCenterData → {docs, completedSessions}. */
export async function studyCenterFromWarehouse(userId) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return null;
  try {
    const docs = await readDocs(api);
    const sessions = await readHistory(api);
    const completedSessions = sessions.filter((s) => s.status === 'completed').length;
    return { docs, completedSessions, offline: true };
  } catch (e) {
    return null;
  }
}

/**
 * Study Log: el shape de fetchStudyLogSessions → array de sesiones.
 *
 * F6.8 T2 (D-F8-4 firmada) — POR EL MOTOR, NO POR EL ARMADO JS. Lo que se pinta
 * ya no es "lo que la foto trajo" sino lo que el MISMO núcleo de
 * `GET /study/history` produce con la verdad que este teléfono tiene:
 *
 *   · las especies LOCALES que el servidor todavía no conoce (entregadas sin
 *     ackear, en vuelo y abandonadas) entran al payload con el doble candado del
 *     dedup, así que una sesión hecha sin señal aparece en la PRIMERA visita;
 *   · el sello de vencidos (D-F8-3) corre antes de listar y se vuelve a derivar
 *     al pintar, igual que en el servidor: un examen cuyo reloj se acabó sale
 *     `completed` (o `abandoned` si el dueño es invitado) en vez de "en curso"
 *     eterno;
 *   · el score adaptive lo deriva el núcleo con la fórmula ÚNICA del producto
 *     (`session_score`), no una copia JS;
 *   · `document_names` sale del join contra `documents` que el núcleo ya hace.
 *
 * FIRMA Y SHAPE INTACTOS: `StudyLog.js` no se tocó. El `is_guest` no cabía en la
 * firma, así que sale de la propia bodega (`user_snapshot`, con comprobación de
 * dueño) — y ante cualquier duda, REGISTRADO, que es lo que el servidor deriva
 * de un `find_one` sin resultado.
 *
 * Bodega vacía sigue devolviendo `[]` (el estado vacío de la pantalla), no
 * `null`: eso no cambia respecto a hoy.
 *
 * F6.8 T2b — LA RED DE DISPONIBILIDAD (cierre de C-1). Ver `studyLogViaBrain` y
 * el `catch` de abajo.
 */
export async function studyLogFromWarehouse(userId) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return null;
  try {
    return await studyLogViaBrain(api, userId);
  } catch (e) {
    // ── RED DE DISPONIBILIDAD (F6.8 T2b, cierre de C-1) ─────────────────────
    //
    // EL RIESGO QUE CUBRE. Resolver por el motor le puso al Study Log offline una
    // dependencia que antes no tenía: Pyodide. En un arranque en frío sin red
    // ésta es la primera vez que el worker se calienta, y si NO arranca —hash del
    // motor que no cuadra, chunk que no cargó, RPC que se vence— la pantalla se
    // quedaba sin ninguna fuente y caía al error de siempre. La foto estaba en
    // disco y nadie la leía.
    //
    // LA CAÍDA ES SOLO ANTE RECHAZO. Un resultado EXITOSO del motor jamás se
    // sustituye (ni siquiera uno vacío), y la LENTITUD NO ES FALLA: el techo lo
    // pone el presupuesto del RPC (engineHost.js:33), no este camino. Por eso el
    // fallback vive en el `catch` y no en un `race` ni en un timer propio.
    //
    // LO QUE ESTA VISTA NO TIENE está declarado arriba de `rowToHistorySession`:
    // sin fusión de pendientes y sin sello de vencidos, es la FOTO DE AYER. La
    // verdad completa vive en el motor; esto es una red de emergencia declarada,
    // no una segunda verdad.
    //
    // F6.8 S-2 (pieza 3) — Y AHORA DEJA RASTRO, Y SE CORRIGE SOLA. Ver
    // `fireStudyLogCorrection` arriba.
    try {
      const foto = await readHistory(api);
      diagLog('SCR.FALLBACK', {
        pantalla: 'study-log', llave: 'study-log',
        motivo: motivoDelRechazo(e), filas: foto.length,
      });
      fireStudyLogCorrection(api, userId);
      return foto;
    } catch (e2) {
      // Ni la foto se pudo leer: `null` y el fetcher decide, como siempre.
      diagLog('SCR.FALLBACK', {
        pantalla: 'study-log', llave: 'study-log',
        motivo: motivoDelRechazo(e), foto: 'ilegible',
      });
      return null;
    }
  }
}

// El camino del MOTOR, aparte para que el `catch` del fallback cubra EXACTAMENTE
// esto y nada más: la carga del módulo, el warm-up, el RPC y su respuesta.
async function studyLogViaBrain(api, userId) {
  const reads = await brainReads();
  const now = reads.readNow();
  const isGuest = await reads.readOwnerIsGuest(api, userId);
  const out = await reads.studyHistoryRead(api, { userId, isGuest, now });
  if (!out) return null;
  fireSettleExpiredExams(userId, now, out.expiredLocalBorn);
  return out.response;
}

// ── F6.8 T2 — LOS ENSAMBLADORES DE ANALÍTICAS ───────────────────────────────
//
// Devuelven EXACTAMENTE la forma que el fetcher del servidor devuelve hoy, para
// que la Tanda 3 los enchufe sin un solo adaptador:
//   · overview  → el objeto de GET /analytics/overview
//   · document  → el objeto de GET /analytics/document/{id}
//   · flashcard → el objeto de POST /study/flashcards/progress-summary
//
// `null` ante bodega vacía, documento inexistente, dueño ajeno, web o cualquier
// fallo: el fetcher decide y la pantalla conserva su estado de hoy. JAMÁS ceros
// inventados.

/** Analytics overview: el shape de GET /analytics/overview. */
export async function analyticsOverviewFromWarehouse(userId, isGuest, opts = {}) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return null;
  try {
    const reads = await brainReads();
    const now = reads.readNow(opts.now);
    const out = await reads.analyticsOverviewRead(api, {
      userId, isGuest, plan: opts.plan, now,
    });
    if (!out) return null;
    fireSettleExpiredExams(userId, now, out.expiredLocalBorn);
    return out.response;
  } catch (e) {
    return null;
  }
}

/**
 * APP-1 B5 (#402): SÓLO los tres totales del par (foto, snapshot), para poder
 * componerlos con los vivos cuando hay señal. No sella vencidos ni monta la
 * respuesta entera — es una lectura de tres números. `null` si la bodega no tiene
 * foto utilizable, y entonces el llamador se queda con lo que dijo el servidor.
 */
export async function analyticsTotalsFromWarehouse(userId) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return null;
  try {
    const reads = await brainReads();
    return await reads.analyticsTotalsRead(api, { userId });
  } catch (e) {
    return null;
  }
}

/** Analytics de un documento: el shape de GET /analytics/document/{id}. */
export async function analyticsDocumentFromWarehouse(userId, isGuest, docId, opts = {}) {
  const api = await openOwnedWarehouse(userId);
  if (!api || !docId) return null;
  try {
    const reads = await brainReads();
    const now = reads.readNow(opts.now);
    const out = await reads.analyticsDocumentRead(api, {
      userId, isGuest, plan: opts.plan, docId, now,
    });
    if (!out) return null;
    fireSettleExpiredExams(userId, now, out.expiredLocalBorn);
    return out.response;
  } catch (e) {
    return null;
  }
}

/**
 * Resumen de flashcards del Study Center (H8): el shape de
 * POST /study/flashcards/progress-summary. No sella nada — su handler tampoco
 * mira una sola sesión— y por eso no dispara el cierre de vencidos; `now` viaja
 * por simetría del contrato y el driver no lo lee.
 */
export async function flashcardSummaryFromWarehouse(userId, documentIds, temas) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return null;
  try {
    const reads = await brainReads();
    // TANDA 32b (#294): `temas` viaja tal cual hasta el lector, que recorta las
    // tarjetas y las conocidas ANTES del motor. Ausente o vacío = el resumen de
    // siempre — la firma nueva es aditiva y quien no la pase no cambia nada.
    const out = await reads.flashcardSummaryRead(api, {
      userId, documentIds, temas, now: reads.readNow(),
    });
    return out ? out.response : null;
  } catch (e) {
    return null;
  }
}

/**
 * TANDA 29 (#274) — LOS TEMAS DE LOS DOCUMENTOS ELEGIDOS, DESDE EL TELÉFONO.
 *
 * Espejo local de `POST /study/topics`, y devuelve el shape EXACTO de esa
 * respuesta (`{documents: [{document_id, filename, topics: [{name,
 * question_count, flashcard_count}]}]}`) para que el selector no tenga ni una
 * rama distinta según de dónde vino la lista.
 *
 * TRES REGLAS COPIADAS DEL SERVIDOR, NO REINVENTADAS:
 *   · la clave de tema es `claveDeTema` (lib/claveDeTema.js), la misma función
 *     con la que el runtime local recorta el pool — así la casilla que se
 *     palomea y las filas que entran a la sesión salen de la MISMA regla;
 *   · el orden de los documentos es el que llegó en `documentIds`;
 *   · dentro de cada documento: primero los temas que coinciden EXACTO con la
 *     taxonomía local del documento (`topics_json`, que el bundle ya baja —
 *     `downloader.js`), en ese orden; luego los que no, alfabéticos; y
 *     "General" siempre al final.
 *
 * Un tema 0/0 no existe aquí por construcción: las claves salen de las filas
 * que la bodega tiene, no de la taxonomía.
 *
 * Sin bodega utilizable (web, dueño ajeno, primer arranque) devuelve `null` y
 * quien llama sigue por su camino normal. Jamás lanza.
 */
export async function topicsFromWarehouse(userId, documentIds) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return null;
  const ids = documentIds || [];
  if (ids.length === 0) return { documents: [] };
  try {
    const ph = ids.map(() => '?').join(', ');
    const [preguntas, tarjetas, docs] = await Promise.all([
      api.query(`SELECT document_id, topic FROM questions WHERE document_id IN (${ph});`, ids),
      api.query(`SELECT document_id, topic FROM flashcards WHERE document_id IN (${ph});`, ids),
      api.query(`SELECT id, filename, topics_json FROM documents WHERE id IN (${ph});`, ids),
    ]);
    // Mapa ANIDADO (documento -> tema -> n) y no una llave concatenada: el
    // nombre de un tema puede llevar cualquier carácter —espacios incluidos,
    // "Cardiovascular System"—, así que no hay separador que se pueda deshacer
    // después sin arriesgarse a partir un nombre por la mitad.
    const contar = (filas) => {
      const cuenta = new Map();
      for (const fila of filas) {
        if (!cuenta.has(fila.document_id)) cuenta.set(fila.document_id, new Map());
        const porTema = cuenta.get(fila.document_id);
        const nombre = claveDeTema(fila);
        porTema.set(nombre, (porTema.get(nombre) || 0) + 1);
      }
      return cuenta;
    };
    const nQ = contar(preguntas);
    const nF = contar(tarjetas);
    const porId = new Map(docs.map((d) => [d.id, d]));
    const salida = [];
    for (const docId of ids) {
      const doc = porId.get(docId);
      if (!doc) continue;
      const qDoc = nQ.get(docId) || new Map();
      const fDoc = nF.get(docId) || new Map();
      const nombres = new Set([...qDoc.keys(), ...fDoc.keys()]);
      const taxonomia = parseJson(doc.topics_json, [])
        .map((t) => (t && typeof t.name === 'string' ? t.name : null))
        .filter((n) => n && n !== 'General');
      const enTaxonomia = [];
      for (const n of taxonomia) {
        if (nombres.has(n) && !enTaxonomia.includes(n)) enTaxonomia.push(n);
      }
      const resto = [...nombres]
        .filter((n) => n !== 'General' && !enTaxonomia.includes(n))
        .sort();
      const cola = nombres.has('General') ? ['General'] : [];
      salida.push({
        document_id: docId,
        filename: doc.filename ?? 'Untitled',
        topics: [...enTaxonomia, ...resto, ...cola].map((name) => ({
          name,
          question_count: qDoc.get(name) || 0,
          flashcard_count: fDoc.get(name) || 0,
        })),
      });
    }
    return { documents: salida };
  } catch (e) {
    return null;
  }
}

/**
 * Dashboard: {docs, planInfo, usage} — planInfo/usage desde la ÚLTIMA FOTO
 * CONOCIDA (plan_snapshot, decisión firmada opción A). Sin snapshot todavía
 * (bodega nueva): null y los widgets muestran su estado vacío actual — sin
 * inventar ceros.
 *
 * F6.7b — LOS DOCS SALEN DE LA FOTO DE LA LISTA, no de las tablas de la bodega.
 * El Panel es verdad de SERVIDOR, y las tablas de la bodega son inventario del
 * TELÉFONO: solo contienen lo que se alcanzó a bajar (ready/partial servibles).
 * Pintar eso como "la lista de tus documentos" borraba de la pantalla, sin
 * avisar, todo doc en `processing` — y también todo doc listo cuya descarga no
 * había ocurrido. La foto conserva el ÚLTIMO ESTADO CONOCIDO de cada uno, que
 * es justo lo que el Panel debe mostrar sin señal.
 *
 * Sin foto todavía (bodega poblada por una versión anterior, o primer arranque
 * sin haber leído nunca la lista con señal) cae al camino de F6.4b — los docs
 * de la bodega — que sigue siendo honesto, solo menos completo.
 */
export async function dashboardFromWarehouse(userId) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return null;
  try {
    const [snapshot, docsPhoto] = await Promise.all([
      readPlanSnapshot(),
      readDocsSnapshot(),
    ]);
    const docs = docsPhoto && docsPhoto.length > 0
      ? sortLibraryDocs(docsPhoto)
      : await readDocs(api);
    // t7-3 (A1, #557): la ultima lista de grupos que llego con red, con la caducidad medida
    // contra el reloj del telefono. Sin foto la llave no existe (el Panel de hoy).
    const grupos = await leerGruposDeBodega(api);
    return {
      docs,
      planInfo: snapshot?.planInfo ?? null,
      usage: snapshot?.usage ?? null,
      ...(Array.isArray(grupos) ? { grupos: gruposSinRed(grupos) } : {}),
      offline: true,
    };
  } catch (e) {
    return null;
  }
}

// ── RPW T5 (P3) — LO QUE EL CEL YA SABE, EL CEL LO MUESTRA ──────────────────
//
// EL HALLAZGO (F1 del smoke): una entrega de examen hecha en el cel no aparecía
// en Analytics en la PRIMERA visita — aparecía al salir y volver. No había magia
// de cache: lo que cambiaba en medio era el SERVIDOR. El acta se guarda local al
// instante, pero el cartero la sube con debounce (2.5 s / 10 s mínimo), y la
// primera visita pedía `/analytics/document/{id}` antes de que el servidor la
// tuviera. La segunda visita, ya subida, la traía.
//
// Los dos ensambladores de abajo son de una especie NUEVA en este archivo: no
// SUSTITUYEN un fetch caído — se FUSIONAN encima de uno exitoso. Por eso el
// lector devuelve `[]` (no null) y la fusión es pura y con identidad: sin nada
// que fusionar, el objeto del servidor sale TAL CUAL y la web no cambia en nada.

// Fila de `local_sessions` (mode=exam, entregada) → el renglón EXACTO que
// `build_exam_analytics` produce (engine_core.py:848-853), más lo que la fusión
// necesita: de qué documentos es y sus aciertos por tema.
function rowToExamDelivery(row) {
  const started = row.started_at;
  const completed = row.completed_at;
  let timeTaken = null;
  if (started && completed) {
    const a = Date.parse(started);
    const b = Date.parse(completed);
    // Mismo redondeo a un decimal que el servidor; ilegible → null, como allá.
    if (Number.isFinite(a) && Number.isFinite(b)) {
      timeTaken = Math.round(((b - a) / 60000) * 10) / 10;
    }
  }
  return {
    session_id: row.id,
    document_ids: parseJson(row.document_ids_json, []),
    results_summary: parseJson(row.results_summary_json, []),
    row: {
      date: completed || started,
      score: row.score ?? 0,
      correct: row.correct_count ?? 0,
      total: row.total_questions ?? 0,
      time_limit: row.time_limit ?? null,
      time_taken_minutes: timeTaken,
    },
  };
}

/**
 * Las entregas de examen LOCALES que el servidor AÚN NO REFLEJA. `[]` en web,
 * `[]` sin bodega y `[]` ante cualquier error: esto pinta encima de un dato que
 * ya llegó, así que jamás puede tumbar la pantalla.
 *
 * LA DEDUPLICACIÓN, por `session_id` y con tres candados independientes:
 *
 *   · `NOT IN study_history` — la foto del servidor. Si el acta ya está ahí, el
 *     servidor la tiene y LA DEL SERVIDOR MANDA (regla firmada).
 *   · sin un solo `local_events` ACUSADO de esa sesión — la evidencia precisa. El
 *     acta viaja junto a los renglones del diario, así que en cuanto UNO va
 *     acusado el servidor ya tiene la sesión (aunque la foto local venga
 *     retrasada, que es la ventana que el primer candado no cubre).
 *   · `acta_acked_at IS NULL` — el GEMELO POR-ACTA del anterior (F6.8 Tanda C-b).
 *     Misma doctrina y misma clase de evidencia, para la especie que no tiene
 *     renglones donde mirarla: un examen entregado EN BLANCO. Ver el veredicto
 *     completo al final de este bloque.
 *
 * ── POR QUÉ ESTE SEGUNDO CANDADO SE QUEDA, Y EL DE LA FUSIÓN DE SESIONES NO
 *    (G-2, la deuda C-2 deja de ser "la misma regla") ─────────────────────────
 *
 * INC-1 obligó a quitar el candado del acuse en `mergeSessionRows`
 * (brain/analyticsReads.js): allá, esconder una fila confirmada que la foto
 * todavía no traía la borraba de la pantalla. Aquí NO se quita, y la razón es
 * QUÉ SE ESTÁ PINTANDO ENCIMA:
 *
 *   · allá la base es LA FOTO (tablas de la bodega), que puede venir horas
 *     retrasada respecto del servidor → esconder por acuse abre la ventana;
 *   · aquí la base es, ÚNICAMENTE, la RESPUESTA VIVA de
 *     `/analytics/document/{id}`. Es MÁS FRESCA que la foto: en cuanto el
 *     cartero recibe el acuse, el servidor ya la incluye en su cálculo.
 *     Deduplicar solo por la foto pintaría la entrega DOS VECES —la del servidor
 *     y la local— hasta la siguiente bajada. El acuse es, aquí, la evidencia
 *     local exacta de "la base que estoy pintando YA la trae", que es la
 *     doctrina firmada de G-2.
 *
 * ── G-3: SIN SEÑAL ESTA FUSIÓN YA NO CORRE ──────────────────────────────────
 *
 * Hasta la T3, lo que devuelve este lector se fusionaba TAMBIÉN encima de la
 * base que arma el MOTOR, y ahí duplicaba: el motor ya fusiona las entregas
 * locales que la foto no trae (`mergeSessionRows`, brain/analyticsReads.js), así
 * que sumarle éstas contaba la misma entrega DOS veces (CONFLICTO 2). El corte
 * lo hace la pantalla, por PROCEDENCIA de la respuesta: cuando el detalle vino
 * de la bodega, la fusión se salta entera (`DETALLE_DESDE_BODEGA`,
 * pages/Analytics.js:202-207 y :384-385).
 *
 * En la T3 este lector NO CAMBIÓ NI UNA LÍNEA — cambió DÓNDE se usa lo que
 * devuelve. Sus candados gobiernan ahora un solo camino, el de la respuesta
 * viva, que es justo donde su deduplicación es la que manda.
 *
 * LA VENTANA QUE ESTO DEJA ABIERTA, ACEPTADA A PROPÓSITO (decisión firmada por
 * Carlos; reporte `g3_f68_unavez_2026-08-01.md` §5.1). Un examen ADOPTADO que se
 * entregó SIN señal y se abre CON señal no se pinta durante los segundos que
 * tarda el cartero en subir el acta: el servidor todavía lo tiene EN CURSO —así
 * que su respuesta viva no lo trae— y el primer candado de aquí lo excluye por
 * estar ya en `study_history`. Cerrarla exigiría reformar ese candado, y el
 * costo no paga una ventana de segundos (el debounce del cartero: 2.5 s / 10 s
 * mínimo). SIN señal el caso ya está resuelto, y por otro camino: lo cubre la
 * precedencia del hecho completado (`localSupersedesPhoto`, G-3).
 *
 * Y no esconde nada de nadie: la base sobre la que esto pinta —la respuesta
 * viva— contiene la entrega acusada. El usuario la ve UNA vez por el camino que
 * corresponda.
 *
 * ── F6.8 TANDA C: EL ACTA SIN RENGLONES YA NO ES LOCAL PARA SIEMPRE (INC-3) ──
 *
 * Aquí decía: «Un acta sin renglones de diario (un examen entregado sin contestar
 * nada) no viaja NUNCA —el cartero manda actas junto a eventos pendientes— así
 * que es local para siempre y aquí entra siempre. Es correcto: el servidor no la
 * tendrá.» Se documentaba como correcta la conducta que Carlos reportó como
 * INC-3: ese examen no salía en el Study Log ni en las analíticas de la web, y en
 * la app solo sin señal.
 *
 * YA NO ES CIERTO. El cartero estrenó el carril de ACTAS HUÉRFANAS
 * (brain/dispatcher.js): un acta cerrada, sin acusar, sin renglones pendientes y
 * sin familia `rejected_final` viaja SOLA en los ciclos que ya existen, y el
 * servidor la materializa y la califica (score 0 limpio, sin anomalía). O sea: el
 * servidor SÍ la va a tener, y por eso este lector ya no puede dar por hecho que
 * es suya en exclusiva.
 *
 * ── EL TERCER CANDADO (F6.8 Tanda C-b) — APLICADO ───────────────────────────
 *
 * LA HISTORIA, porque el orden de los hechos es lo que lo explica. La Tanda C
 * revisó este candado como manda su encargo, y el veredicto fue que el segundo
 * se había quedado CIEGO para la especie nueva. Como esa tanda tenía el SQL
 * fuera de alcance, la línea quedó documentada aquí y a firma de Carlos; la
 * Tanda C-b es esa firma, y ésta es la línea.
 *
 * EL RAZONAMIENTO, con la doctrina G-2 de base:
 *
 *   · el segundo candado existe porque la base que esto pinta es la RESPUESTA
 *     VIVA, más fresca que la foto: en cuanto el servidor conoce la entrega,
 *     deduplicar solo por `study_history` la pintaría DOS VECES hasta la
 *     siguiente bajada;
 *   · su evidencia es «hay un renglón ACUSADO de esta sesión». Un examen
 *     entregado en blanco no tiene ni un `local_events`, así que ese candado no
 *     puede dispararse JAMÁS para él;
 *   · desde la Tanda C sí existe la evidencia equivalente, y es exactamente del
 *     mismo tipo: `local_sessions.acta_acked_at` (schema v8) — el servidor
 *     confirmó ESTA acta. Un renglón acusado y un acta sellada dicen la MISMA
 *     frase, «la base que estoy pintando YA la trae»; solo cambia dónde está
 *     escrita, porque el acta huérfana no tiene renglón donde escribirla.
 *
 * LA VENTANA QUE CIERRA, y que la propia Tanda C había abierto: entre que el
 * cartero sella el acta y la siguiente bajada de `/sync/state` mete la sesión en
 * `study_history`, la respuesta viva YA la trae y este lector TAMBIÉN la
 * devolvía. `mergeExamDeliveries` concatena sin deduplicar por `session_id`
 * (examAnalyticsMerge.js:66), así que la entrega habría contado DOS VECES en
 * `total_exams`, `average_score` y `topic_accuracy`.
 *
 * Y ES SEGURO EN LA DIRECCIÓN CONTRARIA, que es lo que lo vuelve el gemelo
 * correcto y no un simple filtro más: si el transporte llegó pero el ACUSE se
 * perdió (red caída a media respuesta, app matada antes del sello), la fila
 * sigue con `acta_acked_at` NULL y este lector la devuelve — mientras que la
 * respuesta viva, que sí tiene la sesión, la muestra por su cuenta. El usuario
 * la ve UNA vez por el camino que corresponda, JAMÁS cero. El sello es la
 * evidencia de "ya la traen", no un permiso de existir.
 *
 * G-3 SIGUE MANDANDO SIN SEÑAL: esto solo gobierna el camino de la respuesta
 * viva. Sobre la base del MOTOR la fusión no corre (`DETALLE_DESDE_BODEGA`), y
 * ahí la precedencia del hecho completado (`localSupersedesPhoto`) es la que
 * resuelve, igual que antes de esta línea.
 */
export async function examDeliveriesFromWarehouse(userId) {
  const api = await openOwnedWarehouse(userId);
  if (!api) return [];
  try {
    const rows = await api.query(
      `SELECT s.id, s.document_ids_json, s.results_summary_json, s.started_at,
              s.completed_at, s.score, s.correct_count, s.total_questions,
              s.time_limit
         FROM local_sessions s
        WHERE s.mode = 'exam'
          AND s.completed = 1
          AND s.id NOT IN (SELECT id FROM study_history)
          AND NOT EXISTS (
                SELECT 1 FROM local_events e
                 WHERE e.session_id = s.id AND e.upload_state = 'acked')
          AND s.acta_acked_at IS NULL
        ORDER BY s.completed_at DESC;`
    );
    return rows.map(rowToExamDelivery);
  } catch (e) {
    return [];
  }
}

// La FUSIÓN vive en `./examAnalyticsMerge` — pura, sin un solo import, para que
// la pantalla pueda probarse con la fusión de verdad y sólo este lector mockeado.
